import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { authApi, formatError } from "../lib/api";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter your email address");
      return;
    }
    setBusy(true);
    try {
      await authApi.forgotPassword(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-6 py-10">
      <button
        type="button"
        onClick={() => navigate("/auth")}
        data-testid="forgot-back-btn"
        className="text-sm text-[#4F5D75] hover:text-[#2D3142] flex items-center gap-2 mb-6 self-start"
      >
        <ArrowLeft className="w-4 h-4" /> Back to sign in
      </button>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <div className="mb-2">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#277A82]">
            QFresh
          </span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold brand-gradient-text leading-tight tracking-tight">
          Forgot your password?
        </h1>
        <p className="text-[#4F5D75] mt-3 max-w-sm">
          {sent
            ? "We just sent you a reset link if that email is on file."
            : "Enter your email and we'll send you a link to reset it. Link is valid for 30 minutes."}
        </p>
      </motion.div>

      <div className="mt-8" data-testid="forgot-password-page">
        {sent ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="space-y-4"
            data-testid="forgot-success-panel"
          >
            <div className="tup-card p-5 flex gap-3 items-start">
              <CheckCircle2 className="w-5 h-5 text-[#277A82] mt-0.5 shrink-0" />
              <div className="text-sm text-[#2D3142] leading-relaxed">
                If an account exists for <strong>{email}</strong>, a reset link is on its way.
                Check your inbox (and spam folder) — the link works for 30 minutes.
              </div>
            </div>
            <Button
              onClick={() => navigate("/auth")}
              data-testid="forgot-back-to-login"
              className="w-full bg-[#277A82] hover:bg-[#1F5566] text-white h-12"
            >
              Back to sign in
            </Button>
            <button
              type="button"
              onClick={() => {
                setSent(false);
                setEmail("");
              }}
              className="w-full text-center text-sm text-[#4F5D75] hover:text-[#2D3142] pt-1"
            >
              Use a different email
            </button>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            onSubmit={submit}
            className="space-y-4"
          >
            <div>
              <Label className="text-[#2D3142] font-semibold flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-[#277A82]" /> Email
              </Label>
              <Input
                value={email}
                type="email"
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="forgot-email-input"
                className="bg-white border-[#E2E0D8] h-12"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="forgot-submit-btn"
              className="w-full bg-[#277A82] hover:bg-[#1F5566] text-white h-12 mt-2"
            >
              {busy ? "Sending…" : "Send reset link"}
            </Button>

            <Link
              to="/auth"
              className="block w-full text-center text-sm text-[#4F5D75] hover:text-[#2D3142] pt-2"
            >
              Remember it? Sign in instead
            </Link>
          </motion.form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
